import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseAudioManagerReturn {
  isMuted: boolean;
  isPlaying: boolean;
  volume: number;
  autoplayBlocked: boolean;
  toggleMute: () => void;
  setVolume: (vol: number) => void;
  play: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  duckVolume: () => void;
  restoreVolume: () => void;
}

const DUCK_VOLUME = 0.05;
const DEFAULT_VOLUME = 0.3;

/**
 * Custom hook for managing background music playback via HTMLAudioElement.
 *
 * Handles autoplay restrictions by catching play() promise rejection,
 * supports mute toggle, play/pause/resume/stop, and volume ducking
 * for narration coordination. Cleans up the audio element on unmount.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 10.10
 */
export function useAudioManager(musicUrl: string): UseAudioManagerReturn {
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolumeState] = useState<number>(DEFAULT_VOLUME);
  const [autoplayBlocked, setAutoplayBlocked] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef<boolean>(true);
  const targetVolumeRef = useRef<number>(DEFAULT_VOLUME);
  const isDuckedRef = useRef<boolean>(false);

  // Create and configure the audio element once on mount
  useEffect(() => {
    const audio = new Audio(musicUrl);
    audio.loop = true;
    audio.volume = DEFAULT_VOLUME;
    audio.preload = 'auto';
    audioRef.current = audio;

    // Sync isPlaying state with actual audio events
    const handlePlay = () => {
      if (mountedRef.current) setIsPlaying(true);
    };
    const handlePause = () => {
      if (mountedRef.current) setIsPlaying(false);
    };
    const handleEnded = () => {
      if (mountedRef.current) setIsPlaying(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);

      // Full cleanup on unmount
      audio.pause();
      audio.src = '';
      audio.load();
      audioRef.current = null;
    };
  }, [musicUrl]);

  // Cleanup mounted ref
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Start playing the background music from the beginning.
   * Catches autoplay restrictions gracefully — the user can unmute manually.
   */
  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    audio.volume = isDuckedRef.current ? DUCK_VOLUME : targetVolumeRef.current;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          if (mountedRef.current) setAutoplayBlocked(false);
        })
        .catch((err: Error) => {
          console.warn('[useAudioManager] Autoplay blocked by browser:', err.message);
          if (mountedRef.current) setAutoplayBlocked(true);
        });
    }
  }, []);

  /**
   * Pause the music, preserving the current playback position.
   * Requirements: 6.3
   */
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  /**
   * Resume the music from where it was paused.
   * Requirements: 6.4
   */
  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = isDuckedRef.current ? DUCK_VOLUME : targetVolumeRef.current;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          if (mountedRef.current) setAutoplayBlocked(false);
        })
        .catch((err: Error) => {
          console.warn('[useAudioManager] Resume blocked by browser:', err.message);
          if (mountedRef.current) setAutoplayBlocked(true);
        });
    }
  }, []);

  /**
   * Stop the music entirely and reset playback position.
   * Requirements: 6.2
   */
  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  /**
   * Toggle mute/unmute. When unmuting, also attempts to play if not already
   * playing (handles the autoplay-blocked scenario where user clicks unmute).
   * Requirements: 6.6
   */
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setIsMuted((prev) => {
      const newMuted = !prev;
      audio.muted = newMuted;
      return newMuted;
    });
  }, []);

  /**
   * Reduce music volume to allow narration to be clearly audible.
   * Ducks to 15% of the user's chosen volume level.
   * Requirements: 10.10
   */
  const duckVolume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    isDuckedRef.current = true;
    // Duck to 15% of the user's set volume, not a fixed value
    audio.volume = Math.max(0.02, targetVolumeRef.current * 0.15);
  }, []);

  /**
   * Restore music volume to the user's chosen level after narration ends.
   * Requirements: 10.10
   */
  const restoreVolume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    isDuckedRef.current = false;
    audio.volume = targetVolumeRef.current;
  }, []);

  /**
   * Set the base music volume (0.0 to 1.0).
   * Always applies immediately to the audio element.
   * If currently ducked, applies the ducked ratio of the new volume.
   */
  const setVolume = useCallback((vol: number) => {
    const clamped = Math.max(0, Math.min(1, vol));
    targetVolumeRef.current = clamped;
    setVolumeState(clamped);

    const audio = audioRef.current;
    if (!audio) return;

    if (isDuckedRef.current) {
      // Apply ducked ratio of the new volume so slider still has effect during narration
      audio.volume = Math.max(0.02, clamped * 0.15);
    } else {
      audio.volume = clamped;
    }
  }, []);

  return {
    isMuted,
    isPlaying,
    volume,
    autoplayBlocked,
    toggleMute,
    setVolume,
    play,
    pause,
    resume,
    stop,
    duckVolume,
    restoreVolume,
  };
}
